import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import { shareAPI } from '../services/api';
import { ShoppingBagIcon, ShoppingCartIcon, TruckIcon, PhoneIcon, FilmIcon, WifiIcon, GiftIcon, ArrowRightIcon } from '@heroicons/react/24/outline';

const categoryIcons = {
  food: ShoppingBagIcon, transport: TruckIcon, groceries: ShoppingCartIcon,
  entertainment: FilmIcon, internet: WifiIcon, other: GiftIcon
};

const PublicSettlement = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await shareAPI.getData(token);
        setData(response.data);
      } catch (e) {
        setError('Link tidak ditemukan atau sudah kedaluwarsa.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token]);

  const fmt = (n) => 'Rp ' + n.toLocaleString('id-ID', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

  if (loading) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-surface flex flex-col items-center justify-center px-6">
        <p className="text-slate-600 mb-4 text-center">{error || 'Data tidak tersedia'}</p>
        <button onClick={() => navigate('/')} className="text-primary font-semibold hover:underline">Kembali ke Beranda</button>
      </div>
    );
  }

  const totalBills = data.transactions.reduce((sum, t) => sum + t.amount, 0);

  return (
    <div className="min-h-screen bg-surface">
      <div className="w-full max-w-md mx-auto bg-white min-h-screen shadow-2xl sm:shadow-[0_0_60px_rgba(0,0,0,0.1)]">
        {/* Header */}
        <div className="bg-primary text-white px-6 pt-12 pb-8 rounded-b-[32px]">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <p className="text-sm opacity-80 mb-1">Patungin</p>
            <h1 className="text-2xl font-bold mb-2">{data.group_name}</h1>
            <div className="flex items-center gap-2">
              <div className="flex -space-x-2">
                {data.members.slice(0, 4).map((m, i) => (
                  <img key={i} src={`https://api.dicebear.com/9.x/micah/svg?seed=${m}`} alt={m}
                    className="w-8 h-8 rounded-full border-2 border-primary" />
                ))}
              </div>
              <span className="text-sm opacity-80">{data.members.length} anggota</span>
            </div>
          </motion.div>
        </div>

        {/* Summary */}
        <div className="px-6 -mt-6">
          <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.2 }}
            className="bg-white p-5 rounded-3xl shadow-soft border border-border text-center">
            <p className="text-sm text-slate-500 mb-1">Total Pengeluaran</p>
            <p className="text-3xl font-extrabold text-slate-900">{fmt(totalBills)}</p>
            <p className="text-xs text-slate-500 mt-1">{data.transactions.length} transaksi</p>
          </motion.div>
        </div>

        {/* Settlements */}
        {data.settlements.length > 0 && (
          <div className="px-6 mt-8">
            <h2 className="text-lg font-bold text-slate-900 mb-4">Siapa Bayar ke Siapa</h2>
            <div className="space-y-3">
              {data.settlements.map((s, idx) => (
                <motion.div key={idx} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 + idx * 0.1 }}
                  className="flex items-center justify-between p-4 bg-surface rounded-3xl" data-testid={`public-settlement-${idx}`}>
                  <div className="flex items-center gap-3">
                    <img src={`https://api.dicebear.com/9.x/micah/svg?seed=${s.from}`} alt={s.from} className="w-10 h-10 rounded-full" />
                    <div>
                      <p className="font-semibold text-slate-900 text-sm">{s.from}</p>
                      <p className="text-[10px] text-slate-500">bayar ke {s.to}</p>
                    </div>
                  </div>
                  <p className="text-lg font-bold text-danger">{fmt(s.amount)}</p>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {/* Transactions */}
        <div className="px-6 mt-8 pb-12">
          <h2 className="text-lg font-bold text-slate-900 mb-4">Rincian Transaksi</h2>
          <div className="space-y-3">
            {data.transactions.map((t, idx) => {
              const Icon = categoryIcons[t.category] || GiftIcon;
              return (
                <motion.div key={idx} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 + idx * 0.05 }}
                  className="bg-white border border-border rounded-3xl p-4" data-testid={`public-transaction-${idx}`}>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-start">
                        <h4 className="font-semibold text-slate-900 text-sm">{t.description}</h4>
                        <p className="font-bold text-slate-900 text-sm ml-2">{fmt(t.amount)}</p>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">Dibayar oleh {t.paidBy}</p>
                      {t.splits && t.splits.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {t.splits.map((sp, si) => (
                            <span key={si} className="text-[10px] bg-surface px-2 py-0.5 rounded-full text-slate-600">
                              {sp.name}: {fmt(sp.amount)}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* CTA */}
        <div className="px-6 pb-12">
          <button onClick={() => navigate('/register')} data-testid="public-cta-register"
            className="w-full bg-primary text-white py-4 rounded-full font-bold text-lg hover:bg-primary-hover active:scale-95 transition-all shadow-float flex items-center justify-center gap-2">
            Coba Patungin Gratis
            <ArrowRightIcon className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default PublicSettlement;
